/* istanbul ignore file */
// Coverage ignore: this provider is exercised only on real envs (web bundle
// running against a deployed `/config.json`, plus real `vite:preloadError`
// and Apollo error events); unit-testing it would require mocking fetch +
// sessionStorage + timers + a non-trivial React/Router tree. All decision
// logic is extracted into the unit-tested pure modules in this directory
// (`deployCompat.ts`, `reloadGuard.ts`, `routeBoundaryReload.ts`,
// `breakageRecovery.ts`, `validationErrorClassifier.ts`) — this file is thin
// wiring. Behavior is verified by manual smoke test on stg / partner / prod
// after a deploy (MEIS-13220).

import { API_COMPAT_VERSION } from "@msys/common";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useLatest } from "react-use";
import { z } from "zod";
import { getApiCompatVersion, getVersion } from "../../environment.js";
import { decideBreakageRecovery } from "./breakageRecovery.js";
import { decideCompatState } from "./deployCompat.js";
import { isDeployCompatInert } from "./deployCompatInert.js";
import {
  deployCompatRecheckRequested,
  reloadEscalationRequested,
} from "./deployCompatBus.js";
import { canAutoReload, recordAutoReload } from "./reloadGuard.js";
import { shouldReloadAtRouteBoundary } from "./routeBoundaryReload.js";

// Subset of the real /config.json payload this provider needs.
// `.passthrough()` tolerates the rest of `RuntimeConfigSchema`'s required
// fields — this schema intentionally doesn't duplicate that full shape.
const CompatConfigSchema = z
  .object({
    version: z.string().optional(),
    apiCompatVersion: z.number().int().optional(),
  })
  .passthrough();

const INCOMPATIBLE_SNACKBAR_KEY = "msys-deploy-incompatible";

export const DeployCompatibility = () => {
  const { t } = useTranslate("OutdatedDialog");
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const [escalationDialogOpen, setEscalationDialogOpen] = React.useState(false);
  // Whether a compatible update is pending a silent reload at the next
  // route-pathname change (design.md D5). Ref, not state — consumed
  // synchronously inside the pathname-change effect below, and disarming
  // it must not itself trigger a re-render.
  const armedRef = React.useRef(false);
  const lastVersionRef = React.useRef<string | undefined>(undefined);
  const previousPathnameRef = React.useRef<string | undefined>(undefined);
  // Throttles header-triggered re-checks: after a breaking deploy every
  // response an old tab sees carries a differing header, so without a
  // throttle a polling/query-heavy tab would re-fetch /config.json once per
  // GraphQL request until the user reloads. The first observation still
  // checks immediately; the published config flips once per deploy, so one
  // re-check per minute afterwards loses nothing.
  const lastBusRecheckAtRef = React.useRef(0);

  const isInert = isDeployCompatInert();

  const applyCompatCheck = useLatest(
    (payload: { apiCompatVersion?: number; version?: string }) => {
      const nextState = decideCompatState({
        ownCompatVersion: API_COMPAT_VERSION,
        publishedCompatVersion: payload.apiCompatVersion,
        previousVersion: lastVersionRef.current,
        publishedVersion: payload.version,
      });
      lastVersionRef.current = payload.version;

      if (nextState === "compatible-update") {
        armedRef.current = true;
      } else if (nextState === "incompatible") {
        // Disarm any pending compatible-update reload: its D5 justification
        // (the reloaded app behaves identically) no longer holds once the
        // contract broke — the incompatible flow (snackbar / reactive
        // recovery) owns the reload from here.
        armedRef.current = false;
        enqueueSnackbar(
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <span>{t("A new version is available — please reload")}</span>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => window.location.reload()}
            >
              {t("Reload now")}
            </Button>
          </Stack>,
          { variant: "info", persist: true, key: INCOMPATIBLE_SNACKBAR_KEY }
        );
      }
    }
  );

  const checkCompat = useLatest(async () => {
    try {
      // `cache: "no-store"` mirrors `loadRuntimeConfig()` — an intermediary
      // HTTP cache serving a stale payload would mask a real deploy.
      const response = await fetch("/config.json", { cache: "no-store" });
      if (!response.ok) return;

      const payload = CompatConfigSchema.parse(await response.json());
      applyCompatCheck.current(payload);
    } catch (err) {
      console.info(err);
    }
  });

  const recoverFromBreakage = useLatest(() => {
    const action = decideBreakageRecovery(canAutoReload());
    if (action === "reload") {
      recordAutoReload();
      window.location.reload();
    } else {
      setEscalationDialogOpen(true);
    }
  });

  // Re-check /config.json on focus (today's cadence) — plus immediately
  // whenever `customFetch` observes a differing `X-Api-Compat-Version`
  // header (design.md D3: the header is only ever a re-check trigger, never
  // a direct state flip). The initial baseline reuses the config already
  // fetched at bootstrap (`loadRuntimeConfig()` in `app/index.tsx`) instead
  // of an extra /config.json round-trip — while still flagging an
  // already-incompatible tab immediately (e.g. cached HTML/JS served after
  // the backend already rolled forward).
  React.useEffect(() => {
    if (isInert) return;

    applyCompatCheck.current({
      apiCompatVersion: getApiCompatVersion(),
      version: getVersion(),
    });

    const onFocus = () => {
      checkCompat.current();
    };
    window.addEventListener("focus", onFocus);

    const unsubscribe = deployCompatRecheckRequested.subscribe(() => {
      const now = Date.now();
      if (now - lastBusRecheckAtRef.current < 60_000) return;
      lastBusRecheckAtRef.current = now;
      checkCompat.current();
    });

    return () => {
      window.removeEventListener("focus", onFocus);
      unsubscribe();
    };
  }, [isInert, checkCompat, applyCompatCheck]);

  // D6 reactive signals: a lazy-route chunk load failure, and (via the bus)
  // a validation-class GraphQL error the Apollo errorLink classified.
  React.useEffect(() => {
    if (isInert) return;

    const onPreloadError = (event: Event) => {
      event.preventDefault();
      recoverFromBreakage.current();
    };
    window.addEventListener("vite:preloadError", onPreloadError);

    const unsubscribe = reloadEscalationRequested.subscribe(() => {
      setEscalationDialogOpen(true);
    });

    return () => {
      window.removeEventListener("vite:preloadError", onPreloadError);
      unsubscribe();
    };
  }, [isInert, recoverFromBreakage]);

  // D5: silent reload at the route boundary — fires at most once per armed
  // update (disarmed immediately below), skipped while a MUI dialog is
  // open, gated by the shared reload guard.
  React.useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    if (isInert) return;
    // Skip the initial mount — only an actual navigation is the route
    // boundary the design requires; firing here would reload before the
    // user has navigated at all.
    if (
      previousPathname === undefined ||
      previousPathname === location.pathname
    )
      return;

    const isMuiDialogOpen = document.querySelector(".MuiDialog-root") !== null;

    if (
      !shouldReloadAtRouteBoundary({
        armed: armedRef.current,
        isMuiDialogOpen,
        guardAllows: canAutoReload(),
      })
    ) {
      return;
    }

    armedRef.current = false;
    recordAutoReload();
    window.location.reload();
  }, [isInert, location.pathname]);

  return (
    <Dialog
      open={escalationDialogOpen}
      onClose={() => setEscalationDialogOpen(false)}
    >
      <DialogTitle>{t("Update required")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t(
            "Your session is running an outdated version that is no longer compatible with the server. Please reload the page."
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => window.location.reload()}
          color="primary"
          variant="contained"
          autoFocus
        >
          {t("Reload now")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
